import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../PageTitle";
import CashDisbursementStaffNavigation from "./navigation/CashDisbursementStaffNavigation";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	InputNumber,
	Select,
	Button,
	DatePicker,
	message,
	Card,
	Layout,
	ConfigProvider,
	Space,
	Typography,
	List,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const MAX_FILES = 5;
const MAX_BYTES = 50 * 1024 * 1024;
const ALLOWED_EXT = /\.(pdf|jpe?g|png|gif|webp|bmp)$/i;

const multipartConfig = {
	transformRequest: [
		(data, headers) => {
			delete headers["Content-Type"];
			return data;
		},
	],
};

function parseUsdRate(ratePayload) {
	if (!ratePayload || typeof ratePayload.usd_krw !== "string") {
		return null;
	}
	const n = parseFloat(ratePayload.usd_krw.replace(/,/g, ""));
	return Number.isFinite(n) ? n : null;
}

function sumLinesInKrw(lines, rate) {
	const krwSafe = rate || 0;
	let sum = 0;
	for (const row of lines || []) {
		const amt = Number(row?.amount);
		if (!Number.isFinite(amt)) continue;
		if (row?.currency === "USD") {
			sum += amt * krwSafe;
		} else {
			sum += amt;
		}
	}
	return Math.round(sum * 100) / 100;
}

const CreateCashDisbursementVoucher = () => {
	const isMobile = useIsMobile();
	const { id: editId } = useParams();
	const navigate = useNavigate();
	const isEdit = Boolean(editId);
	const [form] = Form.useForm();
	const fileInputRef = useRef(null);
	const [exchangePayload, setExchangePayload] = useState(null);
	const [exchangeError, setExchangeError] = useState("");
	const [submitLoading, setSubmitLoading] = useState(false);
	const [pageLoading, setPageLoading] = useState(!!editId);
	const [draftFiles, setDraftFiles] = useState([]);
	const [existingAttachments, setExistingAttachments] = useState([]);

	const linesWatch = Form.useWatch("lines", form);
	const paymentMethodWatch = Form.useWatch("payment_method", form);

	const usdRate = useMemo(
		() => parseUsdRate(exchangePayload),
		[exchangePayload],
	);

	const runningTotal = useMemo(() => {
		const rows = Array.isArray(linesWatch) ? linesWatch : [];
		return sumLinesInKrw(rows, usdRate);
	}, [linesWatch, usdRate]);

	const fetchExchange = useCallback(async () => {
		setExchangeError("");
		try {
			const res = await useAxios.get(
				"utils/cash-disbursement/exchange-rate/",
			);
			setExchangePayload(res.data);
		} catch (e) {
			const detail =
				e.response?.data?.detail ||
				"환율 정보를 불러오지 못했습니다.";
			setExchangeError(detail);
			setExchangePayload(null);
		}
	}, []);

	useEffect(() => {
		fetchExchange();
	}, [fetchExchange]);

	useEffect(() => {
		let cancelled = false;
		const bootstrap = async () => {
			try {
				const profile = await getCurrentUserProfile();
				const teamDefault =
					(
						profile.employee_team_name || ""
					).trim() ||
					(
						profile.affiliated_department_name ||
						""
					).trim() ||
					"";
				if (!isEdit) {
					form.setFieldsValue({
						document_date: dayjs(),
						team_in_charge: teamDefault,
						person_in_charge:
							profile.name || "",
						payment_method: "카드",
						bank_account_number: "",
						lines: [
							{
								currency: "KRW",
								subject: "",
								amount: undefined,
								description: "",
							},
						],
					});
				}
			} catch {
				message.error(
					"사용자 정보를 불러오지 못했습니다.",
				);
			}

			if (isEdit) {
				try {
					const res = await useAxios.get(
						`utils/cash-disbursement/${editId}/`,
					);
					const v = res.data;
					if (cancelled) return;
					setExistingAttachments(
						v.attachments || [],
					);
					form.setFieldsValue({
						document_date: v.document_date
							? dayjs(v.document_date)
							: dayjs(),
						team_in_charge:
							v.team_in_charge,
						person_in_charge:
							v.person_in_charge,
						payment_method:
							v.payment_method || "카드",
						bank_account_number:
							v.bank_account_number || "",
						lines:
							v.lines?.length > 0
								? v.lines.map(
										(
											l,
										) => ({
											currency: l.currency,
											subject: l.subject,
											amount: Number(
												l.amount,
											),
											description:
												l.description ||
												"",
										}),
									)
								: [
										{
											currency: "KRW",
											subject: "",
											amount: undefined,
											description:
												"",
										},
									],
					});
				} catch {
					message.error(
						"결의서를 불러오지 못했습니다.",
					);
					navigate(
						"/utils/cash-disbursement/my-list",
					);
				} finally {
					if (!cancelled) setPageLoading(false);
				}
			}
		};
		bootstrap();
		return () => {
			cancelled = true;
		};
	}, [editId, form, isEdit, navigate]);

	const onPickFiles = (picked) => {
		const rawList = Array.isArray(picked) ? picked : [picked];
		const next = [...draftFiles];
		let remainingSlots =
			MAX_FILES -
			(isEdit ? existingAttachments.length : 0) -
			next.length;
		if (remainingSlots <= 0) {
			message.warning(
				`첨부는 최대 ${MAX_FILES}개까지 가능합니다.`,
			);
			return;
		}
		for (const file of rawList) {
			if (remainingSlots <= 0) break;
			if (!file || !(file instanceof File)) continue;
			if (file.size > MAX_BYTES) {
				message.error(
					`${file.name}: 파일당 최대 50MB입니다.`,
				);
				continue;
			}
			if (!ALLOWED_EXT.test(file.name)) {
				message.error(
					`${file.name}: 허용되지 않는 형식입니다.`,
				);
				continue;
			}
			next.push(file);
			remainingSlots--;
		}
		setDraftFiles(next);
	};

	const removeDraftAt = (index) => {
		setDraftFiles((prev) => prev.filter((_, i) => i !== index));
	};

	const deleteExistingRemote = async (attachmentId) => {
		try {
			await useAxios.delete(
				`utils/cash-disbursement/${editId}/attachments/${attachmentId}/`,
			);
			setExistingAttachments((prev) =>
				prev.filter((a) => a.id !== attachmentId),
			);
			message.success("첨부가 삭제되었습니다.");
		} catch (e) {
			message.error(
				e.response?.data?.detail ||
					"첨부 삭제에 실패했습니다.",
			);
		}
	};

	const onSubmit = async () => {
		try {
			await form.validateFields();
		} catch {
			return;
		}

		const vals = form.getFieldsValue();
		const lines = (vals.lines || []).map((r) => ({
			subject: (r.subject || "").trim(),
			amount: r.amount,
			currency: r.currency,
			description: (r.description || "").trim(),
		}));

		for (let i = 0; i < lines.length; i++) {
			if (!lines[i].subject) {
				message.error(
					`${i + 1}번째 줄 적요를 입력해 주세요.`,
				);
				return;
			}
			if (
				lines[i].amount === undefined ||
				lines[i].amount === null ||
				Number(lines[i].amount) < 0
			) {
				message.error(
					`${i + 1}번째 줄 금액을 입력해 주세요.`,
				);
				return;
			}
		}

		const pm = (vals.payment_method || "카드").trim();
		if (pm !== "카드" && pm !== "계좌이체") {
			message.error("결제방식을 선택해 주세요.");
			return;
		}
		const bankRaw = (vals.bank_account_number || "").trim();
		if (pm === "계좌이체" && !bankRaw) {
			message.error("계좌이체인 경우 계좌번호를 입력해 주세요.");
			return;
		}

		if (!usdRate && lines.some((l) => l.currency === "USD")) {
			message.error(
				"달러 환율을 불러오지 못해 제출할 수 없습니다. 새로고침 후 다시 시도해 주세요.",
			);
			return;
		}
		if (runningTotal > 100000) {
			message.error(
				"원화 환산 합계가 10만 원을 초과하면 제출할 수 없습니다.",
			);
			return;
		}

		const fd = new FormData();
		fd.append(
			"document_date",
			vals.document_date.format("YYYY-MM-DD"),
		);
		fd.append("team_in_charge", (vals.team_in_charge || "").trim());
		fd.append(
			"person_in_charge",
			(vals.person_in_charge || "").trim(),
		);
		fd.append("payment_method", pm);
		fd.append(
			"bank_account_number",
			pm === "계좌이체" ? bankRaw : "",
		);
		fd.append("lines", JSON.stringify(lines));

		if (isEdit) {
			const retainedIds = (existingAttachments || []).map(
				(a) => a.id,
			);
			fd.append(
				"retained_attachment_ids",
				JSON.stringify(retainedIds),
			);
		}

		draftFiles.forEach((f) => fd.append("files", f, f.name));

		setSubmitLoading(true);
		try {
			if (isEdit) {
				await useAxios.put(
					`utils/cash-disbursement/${editId}/`,
					fd,
					multipartConfig,
				);
				message.success("수정되었습니다.");
			} else {
				await useAxios.post(
					"utils/cash-disbursement/",
					fd,
					multipartConfig,
				);
				message.success("제출되었습니다.");
			}
			navigate("/utils/cash-disbursement/my-list");
		} catch (e) {
			const errBody = e.response?.data;
			let txt =
				errBody?.detail ||
				errBody?.lines?.[0] ||
				errBody?.files ||
				"저장에 실패했습니다.";
			if (typeof errBody === "object" && !errBody?.detail) {
				const parts = [];
				Object.keys(errBody).forEach((k) => {
					const v = errBody[k];
					parts.push(
						`${k}: ${Array.isArray(v) ? v.join(", ") : v}`,
					);
				});
				txt = parts.join("\n") || txt;
			}
			message.error(txt);
		} finally {
			setSubmitLoading(false);
		}
	};

	if (pageLoading) {
		return (
			<ConfigProvider locale={locale}>
				<Layout style={{ minHeight: "100vh" }}>
					<CashDisbursementStaffNavigation />
					<Content className="app-page-content">
						<Text>불러오는 중...</Text>
					</Content>
				</Layout>
			</ConfigProvider>
		);
	}

	return (
		<ConfigProvider locale={locale}>
			<PageTitle
				title={
					isEdit
						? "지출결의서 수정"
						: "지출결의서 작성"
				}
			/>
			<Layout style={{ minHeight: "100vh" }}>
				<CashDisbursementStaffNavigation />
				<Content className="app-page-content">
					<div
						style={{
							maxWidth: 800,
							margin: "0 auto",
							width: "100%",
						}}>
					<div className="page">
					<PageHeader
						title={
							isEdit
								? "지출결의서 수정"
								: "지출결의서 작성"
						}
					/>
					<Card className="surface-card">
						<Space
							direction="vertical"
							style={{
								width: "100%",
							}}
							size="large">
							{exchangeError ? (
								<Text type="danger">
									{
										exchangeError
									}
								</Text>
							) : (
								<Text type="secondary">
									적용
									환율(1달러→원):{" "}
									<strong>
										{usdRate !=
										null
											? usdRate.toLocaleString(
													"ko-KR",
												)
											: "—"}
									</strong>
								</Text>
							)}
							<Text type="secondary">
								원화 기준 합계가{" "}
								<strong>
									10만
									원을
									초과할
									수
									없습니다.
								</strong>{" "}
								달러 항목은 제출
								시점 환율을
								적용합니다.
							</Text>

							<Form
								form={form}
								layout="vertical">
								<Form.Item
									name="document_date"
									label="일자"
									rules={[
										{
											required: true,
											message: "일자를 선택해 주세요.",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item
									name="team_in_charge"
									label="소속 팀"
									rules={[
										{
											required: true,
											message: "소속 팀을 입력해 주세요.",
										},
									]}>
									<Input
										maxLength={
											200
										}
									/>
								</Form.Item>
								<Form.Item
									name="person_in_charge"
									label="담당자"
									rules={[
										{
											required: true,
											message: "담당자를 입력해 주세요.",
										},
									]}>
									<Input
										maxLength={
											100
										}
									/>
								</Form.Item>
								<Form.Item
									name="payment_method"
									label="결제방식"
									rules={[
										{
											required: true,
											message:
												"결제방식을 선택해 주세요.",
										},
									]}>
									<Select>
										<Option value="카드">
											카드
										</Option>
										<Option value="계좌이체">
											계좌이체
										</Option>
									</Select>
								</Form.Item>
								{paymentMethodWatch ===
									"계좌이체" && (
									<Form.Item
										name="bank_account_number"
										label="계좌번호"
										rules={[
											{
												required: true,
												message:
													"계좌번호를 입력해 주세요.",
											},
										]}>
										<Input
											maxLength={
												100
											}
											placeholder="계좌번호"
										/>
									</Form.Item>
								)}

								<Text strong>
									적요 ·
									금액
								</Text>
								<Form.List name="lines">
									{(
										fields,
										{
											add,
											remove,
										},
									) => (
										<Space
											direction="vertical"
											style={{
												width: "100%",
											}}
											size={
												12
											}>
											{fields.map(
												(
													field,
													idx,
												) => (
													<Card
														key={
															field.key
														}
														size="small"
														title={`항목 ${idx + 1}`}
														extra={
															fields.length >
																1 && (
																<Button
																	type="text"
																	danger
																	size="small"
																	onClick={() =>
																		remove(
																			field.name,
																		)
																	}>
																	삭제
																</Button>
															)
														}>
														<Space
															wrap
															align="start">
															<Form.Item
																name={[
																	field.name,
																	"subject",
																]}
																label="적요"
																rules={[
																	{
																		required: true,
																	},
																]}
																style={{
																	flex: 1,
																	minWidth: 180,
																}}>
																<Input
																	maxLength={
																		500
																	}
																/>
															</Form.Item>
															<Form.Item
																name={[
																	field.name,
																	"currency",
																]}
																label="통화"
																rules={[
																	{
																		required: true,
																		message: "선택",
																	},
																]}
																style={{
																	minWidth: 100,
																}}>
																<Select>
																	<Option value="KRW">
																		원
																	</Option>
																	<Option value="USD">
																		달러
																	</Option>
																</Select>
															</Form.Item>
															<Form.Item
																name={[
																	field.name,
																	"amount",
																]}
																label="금액"
																rules={[
																	{
																		required: true,
																	},
																]}
																style={{
																	width: 160,
																}}>
																<InputNumber
																	min={
																		0
																	}
																	style={{
																		width: "100%",
																	}}
																/>
															</Form.Item>
															<Form.Item
																name={[
																	field.name,
																	"description",
																]}
																label="비고"
																style={{
																	flex: 1,
																	minWidth: 160,
																}}>
																<Input
																	maxLength={
																		500
																	}
																/>
															</Form.Item>
														</Space>
													</Card>
												),
											)}
											<Button
												type="dashed"
												onClick={() =>
													add(
														{
															currency: "KRW",
															subject: "",
															amount: undefined,
															description:
																"",
														},
													)
												}
												block
												icon={
													<PlusOutlined />
												}>
												항목
												추가
											</Button>
										</Space>
									)}
								</Form.List>

								<div
									style={{
										marginTop: 16,
									}}>
									<Text
										strong
										style={{
											fontSize: 16,
										}}>
										금액
										합계(원화
										환산):{" "}
										{runningTotal.toLocaleString(
											"ko-KR",
										)}{" "}
										원
									</Text>
								</div>

								{isEdit &&
									existingAttachments.length >
										0 && (
										<div
											style={{
												marginTop: 24,
											}}>
											<Text
												strong>
												등록된
												영수증
											</Text>
											<List
												size="small"
												dataSource={
													existingAttachments
												}
												style={{
													marginTop: 8,
												}}
												renderItem={(
													item,
												) => (
													<List.Item
														actions={[
															<Button
																key="del"
																type="link"
																danger
																size="small"
																icon={
																	<DeleteOutlined />
																}
																onClick={() =>
																	deleteExistingRemote(
																		item.id,
																	)
																}>
																삭제
															</Button>,
														]}>
														{
															item.original_filename
														}
													</List.Item>
												)}
											/>
										</div>
									)}

								<div
									style={{
										marginTop: 16,
									}}>
									<Text
										strong>
										영수증
										첨부
									</Text>
									<input
										ref={
											fileInputRef
										}
										type="file"
										multiple
										accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp"
										style={{
											display: "none",
										}}
										onChange={(
											e,
										) => {
											const arr =
												Array.from(
													e
														.target
														.files ||
														[],
												);
											if (
												arr.length
											)
												onPickFiles(
													arr,
												);
											e.target.value =
												"";
										}}
									/>
									<div
										style={{
											marginTop: 8,
										}}>
										<Button
											onClick={() =>
												fileInputRef.current?.click()
											}>
											파일
											선택
											(PDF
											또는
											이미지,
											최대{" "}
											{
												MAX_FILES
											}
											개,
											파일당
											50MB)
										</Button>
										{draftFiles.length >
											0 && (
											<List
												size="small"
												style={{
													marginTop: 8,
												}}
												dataSource={draftFiles.map(
													(
														f,
														i,
													) => ({
														key: i,
														name: f.name,
														index: i,
													}),
												)}
												renderItem={(
													item,
												) => (
													<List.Item
														actions={[
															<Button
																key="rm"
																type="link"
																size="small"
																onClick={() =>
																	removeDraftAt(
																		item.index,
																	)
																}>
																제거
															</Button>,
														]}>
														{
															item.name
														}
													</List.Item>
												)}
											/>
										)}
									</div>
								</div>

								<Space
									style={{
										marginTop: 24,
									}}>
									<Button
										type="primary"
										loading={
											submitLoading
										}
										onClick={
											onSubmit
										}>
										{isEdit
											? "수정 저장"
											: "제출"}
									</Button>
									<Button
										onClick={() =>
											navigate(
												"/utils/cash-disbursement/my-list",
											)
										}>
										취소
									</Button>
								</Space>
							</Form>
						</Space>
					</Card>
					</div>
					</div>
				</Content>
			</Layout>
		</ConfigProvider>
	);
};

export default CreateCashDisbursementVoucher;
